import { Box, Center, Flex, Icon, Text } from '@chakra-ui/react';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { useFormikContext } from 'formik';

import {
  SchemaQuestion,
  SchemaStep,
  isMultiValue,
  isQuestionVisible,
  labelForValue,
} from './encodeDecodeAnswers';
import {
  BOOLEAN_GOOD_VALUES,
  CONDITION_GOOD_VALUES,
  isConditionGoodValue,
} from './steps/conditionGoodValues';

type ConditionReportDoneProps = {
  steps: SchemaStep[];
  onEdit: () => void;
};

type AnswerTone = 'good' | 'warn' | 'neutral';

type RenderedAnswer = {
  label: string;
  tone: AnswerTone;
};

const renderAnswer = (
  q: SchemaQuestion,
  values: Record<string, unknown>,
): RenderedAnswer | null => {
  const raw = values[q.questionId];

  if (q.type === 'multi_select' && isMultiValue(raw)) {
    if (raw.noIssues) {
      const exclusiveLabel = q.options?.find((o) => o.exclusive)?.label ?? 'No issues';
      return { label: exclusiveLabel, tone: 'good' };
    }
    if (raw.issues.length === 0) return null;
    const labels = raw.issues.map((v) => labelForValue(q.options, v));
    return { label: labels.join(', '), tone: 'warn' };
  }

  if (q.type === 'boolean') {
    if (raw !== true && raw !== false) return null;
    const good = BOOLEAN_GOOD_VALUES[q.questionId];
    const tone: AnswerTone = good == null ? 'neutral' : raw === good ? 'good' : 'warn';
    return { label: raw ? 'Yes' : 'No', tone };
  }

  if (q.type === 'enum') {
    if (typeof raw !== 'string' || raw === '') return null;
    const label = labelForValue(q.options, raw);
    const hasTone = CONDITION_GOOD_VALUES[q.questionId] != null;
    const tone: AnswerTone = hasTone
      ? isConditionGoodValue(q.questionId, raw)
        ? 'good'
        : 'warn'
      : 'neutral';
    return { label, tone };
  }

  if (q.type === 'number' && typeof raw === 'number' && Number.isFinite(raw)) {
    return { label: Number(raw).toLocaleString(), tone: 'neutral' };
  }

  if (typeof raw === 'string' && raw.trim() !== '') {
    return { label: raw.trim(), tone: 'warn' };
  }

  return null;
};

const toneColor = (tone: AnswerTone) => (tone === 'warn' ? 'darkGoldenrod' : 'grayDark');

const SummaryGroup = ({ step, values }: { step: SchemaStep; values: Record<string, unknown> }) => {
  const questions = step.questions.filter((q) => q.active && isQuestionVisible(q, values));
  if (questions.length === 0) return null;

  return (
    <Box mb={5}>
      <Text
        fontSize="xs"
        fontWeight="700"
        color="grayLight"
        textTransform="uppercase"
        letterSpacing="0.05em"
        mb={2}
      >
        {step.stepLabel}
      </Text>
      <Box
        bg="grayLightBackground"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="12px"
        p="14px 16px"
      >
        {questions.map((q, i) => {
          const ans = renderAnswer(q, values);
          return (
            <Flex
              key={q.questionId}
              justify="space-between"
              align="flex-start"
              gap={3}
              py="6px"
              borderBottom={i < questions.length - 1 ? '1px solid' : undefined}
              borderColor="gray.200"
            >
              <Text fontSize="sm" color="grayLight" flexShrink={0}>
                {q.type === 'text' ? 'Why' : q.label}
              </Text>
              <Text
                fontSize="sm"
                fontWeight="600"
                color={ans ? toneColor(ans.tone) : 'grayLight'}
                textAlign="right"
                wordBreak="break-word"
              >
                {ans?.label ?? '—'}
              </Text>
            </Flex>
          );
        })}
      </Box>
    </Box>
  );
};

const ConditionReportDone = ({ steps, onEdit }: ConditionReportDoneProps) => {
  const { values } = useFormikContext<Record<string, unknown>>();

  return (
    <Box>
      <Box textAlign="center" pt={2} pb={4}>
        <Center
          w="64px"
          h="64px"
          borderRadius="full"
          bg="seafoamLight"
          color="leaseEndGreen"
          mx="auto"
          mb={4}
        >
          <Icon as={FaCheck} boxSize="28px" />
        </Center>
        <Text fontSize="xl" fontWeight="800" letterSpacing="-0.02em" color="grayDark" mb={1}>
          Condition report done
        </Text>
        <Text fontSize="sm" color="grayLight" maxW="320px" mx="auto">
          Nice work. Now add photos and you can set your price.
        </Text>
      </Box>

      <Box px={{ base: 0, md: 2 }} pb={0}>
        {steps.map((step) => (
          <SummaryGroup key={step.stepId} step={step} values={values} />
        ))}

        <Flex justify="center" pt={1}>
          <Text
            as="button"
            type="button"
            fontSize="sm"
            color="oceanBoatBlue"
            fontWeight="600"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={onEdit}
          >
            Edit my answers
          </Text>
        </Flex>
      </Box>
    </Box>
  );
};

export default ConditionReportDone;
